import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useListState } from '@mantine/hooks';
import { BlockStyleDto, ContractDto, TextBlockDto } from '../../Types/DocumentT';
import { ContractS } from '../ContractS';
import { DocumentS } from '../../Templates/Documents/DocumentS';
import { AgreementDTO, DocumentDto } from '../DocumentT';

interface ContractContextProps {
  loading: boolean;
  movingBlock: boolean;
  contract: ContractDto | null;
  bookitupDocument: DocumentDto | null;
  aboveBlocks: TextBlockDto[];
  belowBlocks: TextBlockDto[];
  handleChange: (patch: Partial<ContractDto>) => void;
  savePatch: (patch: Partial<ContractDto>) => void;
  changeTextBlock: (blockId: number, index: number, isAbove: boolean, patch: Partial<TextBlockDto>) => Promise<boolean>;
  addBlock: (above: boolean, afterBlock?: number, textBlock?: Partial<TextBlockDto>) => Promise<boolean>;
  moveBlock: (from: number, to: number, fromBelow: boolean, toBelow: boolean) => void;
  deleteBlock: (index: number, isAbove?: boolean) => void;
  insertPositions: (blockId: number, documentId: number) => void;
  changeBlockStyle: (
    blockId: number,
    index: number,
    isAbove: boolean,
    prop: keyof TextBlockDto,
    patch: Partial<BlockStyleDto>,
  ) => void;
}

export const ContractContext = createContext<ContractContextProps>({} as ContractContextProps);

export const ContractContextProvider = () => {
  const { contractId } = useParams();
  const [contract, setContract] = useState<ContractDto | null>(null);
  const [bookitupDocument, setBookitupDocument] = useState<DocumentDto | null>(null);
  const [operationStatus, setStatus] = useState({
    loadingTemplate: false,
    movingBlock: false,
  });
  const [aboveBlocks, aboveHandlers] = useListState<TextBlockDto>([]);
  const [belowBlocks, belowHandlers] = useListState<TextBlockDto>([]);
  const { documentId } = contract ?? {};

  useEffect(() => {
    if (contractId) {
      setStatus((prev) => ({ ...prev, loadingTemplate: true }));
      ContractS.fetchById(Number(contractId))
        .then((resJSON) => {
          if (resJSON) {
            const { aboveTextBlocks, belowTextBlocks } = resJSON;
            aboveHandlers.setState(aboveTextBlocks);
            belowHandlers.setState(belowTextBlocks);
            setContract(resJSON);
          }
        })
        .finally(() => setStatus((prev) => ({ ...prev, loadingTemplate: false })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  useEffect(() => {
    if (documentId) {
      DocumentS.fetchById(documentId).then(setBookitupDocument);
    }
  }, [documentId]);

  const savePatch = async (patch: Partial<ContractDto>) => {
    if (contractId) {
      const resJSON = await ContractS.edit(Number(contractId), patch);
      if (resJSON) {
        setContract(resJSON);
      }
    }
  };

  const handleChange = (patch: Partial<ContractDto>) => {
    setContract((prev) => {
      if (prev) {
        return { ...prev, ...patch };
      }
      return null;
    });
    const { aboveTextBlocks, belowTextBlocks } = patch;
    if (aboveTextBlocks) {
      aboveHandlers.setState(aboveTextBlocks);
    }
    if (belowTextBlocks) {
      belowHandlers.setState(belowTextBlocks);
    }
  };

  const changeTextBlock = async (blockId: number, index: number, isAbove: boolean, patch: Partial<TextBlockDto>) => {
    const resJSON = await ContractS.editTextBlock(blockId, patch);
    if (resJSON) {
      const { setItem } = isAbove ? aboveHandlers : belowHandlers;
      setItem(index, resJSON);
      return true;
    }
    return false;
  };

  const addBlock = async (above: boolean, afterBlock?: number, textBlock?: Partial<TextBlockDto>) => {
    if (contractId) {
      const { addBlockAbove, addBlockBelow } = ContractS;
      const handler = above ? addBlockAbove : addBlockBelow;
      const resJSON = await handler(Number(contractId), afterBlock, textBlock);
      if (resJSON) {
        if (above) {
          aboveHandlers.setState(resJSON.aboveTextBlocks);
        } else {
          belowHandlers.setState(resJSON.belowTextBlocks);
        }
        return true;
      }
    }
    return false;
  };

  const moveBlock = async (from: number, to: number, fromBelow: boolean, toBelow: boolean) => {
    if (contractId) {
      setStatus((prev) => ({ ...prev, movingBlock: true }));
      const resJSON = await ContractS.moveBlock(Number(contractId), from, to, fromBelow, toBelow);
      if (resJSON) {
        const { aboveTextBlocks, belowTextBlocks } = resJSON;
        aboveHandlers.setState(aboveTextBlocks);
        belowHandlers.setState(belowTextBlocks);
      }
      setStatus((prev) => ({ ...prev, movingBlock: false }));
    }
  };

  const deleteBlock = (index: number, isAbove = false) => {
    const { remove } = isAbove ? aboveHandlers : belowHandlers;
    remove(index);
  };

  const insertPositions = async (blockId: number, docId: number) => {
    if (contractId) {
      const resJSON = await ContractS.insertPositions(blockId, Number(contractId), docId);
      if (resJSON) {
        const { aboveTextBlocks, belowTextBlocks } = resJSON;
        aboveHandlers.setState(aboveTextBlocks);
        belowHandlers.setState(belowTextBlocks);
        setContract(resJSON);
      }
    }
  };

  const changeBlockStyle = async (
    blockId: number,
    index: number,
    isAbove: boolean,
    prop: keyof TextBlockDto,
    patch: Partial<BlockStyleDto>,
  ) => {
    const resJSON = await ContractS.editBlockStyle(blockId, patch);
    if (resJSON) {
      const { setItemProp } = isAbove ? aboveHandlers : belowHandlers;
      setItemProp(index, prop, resJSON);
    }
  };

  const value = useMemo(
    () => ({
      loading: operationStatus.loadingTemplate,
      movingBlock: operationStatus.movingBlock,
      contract,
      aboveBlocks,
      belowBlocks,
      bookitupDocument,
      handleChange,
      savePatch,
      changeTextBlock,
      addBlock,
      moveBlock,
      deleteBlock,
      insertPositions,
      changeBlockStyle,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operationStatus, contract, bookitupDocument, aboveBlocks, belowBlocks],
  );

  return (
    <ContractContext.Provider value={value}>
      <Outlet />
    </ContractContext.Provider>
  );
};

export const useAgreements = () => {
  const { contract, handleChange } = useContext(ContractContext);
  const { agreements } = contract ?? { agreements: [] };

  const append = (agreement: AgreementDTO) => {
    handleChange({ agreements: [...agreements, agreement] });
  };

  const remove = (agreementId: string) => {
    const tmp = agreements.filter((a) => a.id !== agreementId);
    handleChange({ agreements: tmp });
  };

  const patch = (agreement: AgreementDTO) => {
    const tmp = agreements.map((a) => (a.id !== agreement.id ? a : agreement));
    handleChange({ agreements: tmp });
  };

  return { agreements, append, remove, patch };
};
